/**
 * Product comparison.
 *
 * One shared selection drives three surfaces that can appear together on the
 * same page:
 *
 *   - Compare controls on collection product cards
 *     (snippets/product-card-compare-control.liquid)
 *   - the floating tray (snippets/comparison-tray.liquid)
 *   - the comparison table (sections/product-comparison.liquid)
 *
 * Selection state lives in the URL wherever the comparison table is on the page
 * (?products=handle-a,handle-b), so a comparison survives a refresh and can be
 * shared, and in sessionStorage everywhere else so filtering, sorting and
 * paging a collection never drop a selection. The URL is authoritative on load.
 *
 * Comparison columns are not shipped with the page. Each selected product's
 * column is pulled from the Section Rendering API
 * (/products/<handle>?section_id=product-comparison-column) and its fragments
 * are distributed into the server-rendered row skeleton, so no product data is
 * duplicated into JSON and nothing is fetched for products nobody compares.
 *
 * The script is loaded by both the section and the tray, so it guards against
 * running twice.
 */
(function () {
  if (window.K9Comparison) return;

  var STORAGE_KEY = "k9:comparison";
  var SECTION_ID = "product-comparison-column";
  var DESKTOP_MAX = 3;
  // The theme's close icon (same markup as the drawers and modals).
  var CLOSE_ICON =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64">' +
    '<path d="M19 17.61l27.12 27.13m0-27.12L19 44.74"/></svg>';
  var MOBILE_MAX = 2;
  var DESKTOP_QUERY = "(min-width: 768px)";

  // handle -> { id, title, image, url }. Populated from whatever card, drawer
  // row or fetched column the handle was first seen on, so the tray can show a
  // thumbnail without a second request for a product already on the page.
  var meta = {};
  var handles = [];
  var columnCache = {};
  var subscribers = [];

  function maxSelectable() {
    return window.matchMedia(DESKTOP_QUERY).matches ? DESKTOP_MAX : MOBILE_MAX;
  }

  /**
   * The URL is only used as comparison state on a page whose job is the
   * comparison. On a collection the URL belongs to Shopify's filtering, and on
   * a product page to the product, so those keep the selection in
   * sessionStorage only.
   */
  function urlHoldsSelection() {
    return (
      !!document.querySelector("product-comparison") &&
      !document.querySelector("#CollectionAjaxResult") &&
      !document.querySelector('[data-section-type="product"]')
    );
  }

  function readStorage() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function writeStorage() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ handles: handles, meta: meta }));
    } catch (error) {
      /* Private browsing and full quotas are not worth failing a comparison over. */
    }
  }

  function writeUrl() {
    if (!urlHoldsSelection()) return;
    var url = new URL(window.location.href);
    if (handles.length) {
      url.searchParams.set("products", handles.join(","));
    } else {
      url.searchParams.delete("products");
    }
    window.history.replaceState({}, "", url);
  }

  /* -------------------------------------------------------------- analytics */

  /**
   * Every event goes into Shopify Customer Events. The theme never touches the
   * data layer itself: the GTM custom pixel
   * (.docs/tracking/consentmo-gtm-custom-pixel.js) subscribes to these names
   * and pushes them on, the same route as sticky_atc_click and
   * chew_level_filter_click. Four events, one funnel: select → compare →
   * comparison page view → View Product. See .docs/tracking/comparison-kpis.md.
   *
   * Product ids, handles and counts only — never anything about the shopper.
   */
  var INTERACTION_EVENTS = ["comparison_product_select", "comparison_compare_click", "comparison_view_product"];
  var USED_FLAG = "k9:comparison-used";
  var NAVIGATE_DELAY = 250;

  function track(name, detail) {
    var payload = Object.assign(
      {
        device_type: deviceType(),
        page_type: pageType(),
        selected_count: handles.length
      },
      detail
    );
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));
    if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === "function") {
      Shopify.analytics.publish(name, payload);
    }
    if (INTERACTION_EVENTS.indexOf(name) > -1) markComparisonUsed();
  }

  /**
   * For events fired by a click that leaves the page: give the pixel a moment
   * to hand the event on before unload cuts it off. New-tab clicks don't
   * unload anything, so they go straight through.
   */
  function trackThenNavigate(event, name, detail, url) {
    track(name, detail);
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1)) return;
    if (event) event.preventDefault();
    window.setTimeout(function () {
      window.location.href = url;
    }, NAVIGATE_DELAY);
  }

  function pageType() {
    var path = window.location.pathname;
    if (path.indexOf("/collections/") > -1 && path.indexOf("/products/") === -1) return "collection";
    if (path.indexOf("/products/") > -1) return "product";
    if (path.indexOf("/pages/") > -1) return "page";
    return "other";
  }

  /**
   * Stamps the cart once per browser session with a hidden `_comparison_used`
   * attribute, which carries through to the order. That is the Shopify-side
   * half of the conversion and revenue KPIs: GA4 segments sessions by event,
   * Shopify can split orders by this attribute without relying on GA4's
   * session stitching. Underscore-prefixed, so checkout doesn't show it.
   */
  function markComparisonUsed() {
    try {
      if (sessionStorage.getItem(USED_FLAG)) return;
      sessionStorage.setItem(USED_FLAG, "1");
    } catch (error) {
      return;
    }
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { _comparison_used: "yes" } }),
      // Survives the navigation when the first interaction is View Product.
      keepalive: true
    }).catch(function () {
      /* Attribution is best-effort; never block the comparison on it. */
    });
  }

  function deviceType() {
    return window.matchMedia(DESKTOP_QUERY).matches ? "desktop" : "mobile";
  }

  function notify() {
    subscribers.forEach(function (callback) {
      callback();
    });
  }

  function commit() {
    writeStorage();
    writeUrl();
    notify();
  }

  function rememberMeta(handle, details) {
    if (!details) return;
    meta[handle] = Object.assign({}, meta[handle], details);
  }

  function isSelected(handle) {
    return handles.indexOf(handle) > -1;
  }

  function toggle(handle, details, placement) {
    var index = handles.indexOf(handle);
    if (index > -1) {
      handles.splice(index, 1);
    } else {
      if (handles.length >= maxSelectable()) {
        announce(
          "You can compare up to " +
            maxSelectable() +
            " products. Remove one to add another."
        );
        return false;
      }
      rememberMeta(handle, details);
      handles.push(handle);
      announce(
        ((meta[handle] || {}).title || "Product") +
          " added to the comparison. " +
          handles.length +
          " of " +
          maxSelectable() +
          " selected."
      );
      track("comparison_product_select", {
        product_id: (meta[handle] || {}).id,
        product_title: (meta[handle] || {}).title,
        slot: handles.length,
        placement: placement
      });
    }
    commit();
    return true;
  }

  function clear(placement) {
    if (!handles.length) return;
    handles = [];
    commit();
  }

  /**
   * Single polite live region shared by every surface — selection limits and
   * removals need announcing, and a per-component region would talk over
   * itself.
   */
  var liveRegion = null;
  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement("p");
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.className = "sr-only";
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = "";
    window.setTimeout(function () {
      liveRegion.textContent = message;
    }, 60);
  }

  function fetchColumn(handle) {
    if (columnCache[handle]) return columnCache[handle];
    columnCache[handle] = fetch(
      "/products/" + encodeURIComponent(handle) + "?section_id=" + SECTION_ID,
      { headers: { "X-Requested-With": "XMLHttpRequest" } }
    )
      .then(function (response) {
        if (!response.ok) throw new Error("Comparison column " + response.status);
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, "text/html");
        var column = parsed.querySelector("[data-comparison-column]");
        if (!column) throw new Error("Comparison column missing for " + handle);
        rememberMeta(handle, {
          id: column.dataset.productId,
          title: column.dataset.productTitle
        });
        return column;
      })
      .catch(function (error) {
        // A deactivated or unpublished handle should quietly drop out of the
        // comparison rather than break the table (FR-27).
        delete columnCache[handle];
        console.warn("[comparison]", error.message);
        return null;
      });
    return columnCache[handle];
  }

  /* -------------------------------------------------------------- selection */

  document.addEventListener("click", function (event) {
    var control = event.target.closest("[data-comparison-select]");
    if (control) {
      event.preventDefault();
      toggle(
        control.dataset.comparisonSelect,
        {
          id: control.dataset.productId,
          title: control.dataset.productTitle,
          image: imageFromControl(control)
        },
        control.dataset.placement || "comparison"
      );
      return;
    }

    var remove = event.target.closest("[data-comparison-remove]");
    if (remove) {
      event.preventDefault();
      var handle = remove.dataset.comparisonRemove;
      var title = (meta[handle] || {}).title || "Product";
      toggle(handle, null, remove.dataset.placement || "comparison");
      announce(title + " removed from the comparison.");
      returnFocus(remove);
      return;
    }

    var clearAll = event.target.closest("[data-comparison-clear]");
    if (clearAll) {
      event.preventDefault();
      clear(clearAll.dataset.placement || "tray");
    }
  });

  /**
   * The remove control disappears with the column it sits in, so focus has to
   * go somewhere deliberate: the nearest remaining remove control, else the
   * surface's own heading-level control.
   */
  function returnFocus(removedControl) {
    var root = removedControl.closest("product-comparison, comparison-tray") || document;
    window.requestAnimationFrame(function () {
      var next =
        root.querySelector("[data-comparison-remove]") ||
        root.querySelector("[data-comparison-open-drawer]") ||
        root.querySelector("[data-comparison-tray-toggle]");
      if (next) next.focus();
    });
  }

  /**
   * Reuse the thumbnail already rendered with the control — the card image, or
   * the image inside a drawer row — so adding to the tray costs no extra request.
   */
  function imageFromControl(control) {
    var scope = control.closest("product-card") || control;
    var image = scope.querySelector("img");
    return image ? image.currentSrc || image.src : null;
  }

  /* --------------------------------------------------------- card controls */

  function syncControls() {
    var limit = maxSelectable();
    document.querySelectorAll("[data-comparison-select]").forEach(function (control) {
      var handle = control.dataset.comparisonSelect;
      var selected = isSelected(handle);
      control.setAttribute("aria-pressed", String(selected));
      control.disabled = !selected && handles.length >= limit;

      var label = control.querySelector("[data-comparison-select-label]");
      if (label) label.textContent = selected ? "Selected" : "Compare";
    });
  }

  /**
   * Product titles break after the ™ the same way the collection cards do
   * (snippets/product-grid-item.liquid): the range name on its own line.
   */
  function titleHtml(title) {
    var parts = String(title).split("™");
    if (parts.length < 2) return escapeHtml(title);
    return '<span class="block">' + escapeHtml(parts[0]) + "™</span>" + escapeHtml(parts.slice(1).join("™"));
  }

  /* -------------------------------------------------------------- the tray */

  /**
   * The Klaviyo Customer Hub and accessiBe launchers sit in the corner the tray
   * covers. While the panel is open they are hidden; while it is collapsed
   * they are lifted clear of the title bar, so the chevron stays uncovered.
   *
   * Klaviyo's launcher is light DOM, so CSS in snippets/comparison-tray.liquid
   * does both off the tray's state and the --comparison-launcher-lift variable.
   * accessiBe's trigger lives inside <access-widget-ui>'s shadow tree, and the
   * host's own display, visibility, opacity and transform are all pinned by an
   * !important :host rule, so no document CSS reaches it. A stylesheet adopted
   * into that shadow root can, and the tray rewrites it on every change.
   */
  var launcherSheet = new CSSStyleSheet();

  function adoptLauncherSheet() {
    document.querySelectorAll("access-widget-ui").forEach(function (host) {
      var root = host.shadowRoot;
      if (root && root.adoptedStyleSheets.indexOf(launcherSheet) === -1) {
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat(launcherSheet);
      }
    });
  }

  function placeLaunchers(hidden, lift) {
    document.documentElement.style.setProperty("--comparison-launcher-lift", lift + "px");
    launcherSheet.replaceSync(
      hidden
        ? ".trigger-button{display:none !important}"
        : ".trigger-button{translate:0 -" + lift + "px !important}"
    );
  }

  class TrayElement extends HTMLElement {
    connectedCallback() {
      var tray = this;
      this.panel = this.querySelector("[data-comparison-tray-panel]");
      this.toggleButton = this.querySelector("[data-comparison-tray-toggle]");
      this.chevron = this.querySelector("[data-comparison-tray-chevron]");

      this.toggleButton.addEventListener("click", function () {
        var expanded = tray.toggleButton.getAttribute("aria-expanded") === "true";
        tray.toggleButton.setAttribute("aria-expanded", String(!expanded));
        tray.panel.hidden = expanded;
        tray.syncLaunchers();
        tray.chevron.style.transform = expanded ? "rotate(180deg)" : "";
        tray.reserveSpace();
      });

      this.querySelector("[data-comparison-compare]").addEventListener("click", function () {
        if (handles.length < 2) return;
        var target = new URL(tray.dataset.compareUrl, window.location.origin);
        target.searchParams.set("products", handles.join(","));
        trackThenNavigate(
          null,
          "comparison_compare_click",
          { placement: "collection", compared_products: handles.join(",") },
          target.toString()
        );
      });

      // accessiBe loads with the deferred scripts and mounts its hosts later.
      new MutationObserver(adoptLauncherSheet).observe(document.body, { childList: true });
      adoptLauncherSheet();

      this.render();
      subscribers.push(function () {
        tray.render();
      });
    }

    render() {
      var count = handles.length;
      this.classList.toggle("hidden", count === 0);
      this.syncLaunchers();

      this.querySelectorAll("[data-comparison-count]").forEach(function (node) {
        node.textContent = String(count);
      });

      var slots = this.querySelector("[data-comparison-tray-products]");
      slots.style.setProperty("--cmp-cols", String(maxSelectable()));
      slots.innerHTML =
        handles
          .map(function (handle) {
            var product = meta[handle] || {};
            var title = product.title || handle;
            return (
              '<div class="relative grid min-h-[120px] place-items-center gap-1 rounded-[3px] bg-[#F2F2F2] p-2 text-center md:min-h-[170px] md:p-3.5">' +
              '<button type="button" class="absolute right-1 top-1 z-[1] flex h-9 w-9 items-center justify-center" ' +
              'data-comparison-remove="' +
              escapeAttribute(handle) +
              '" data-placement="tray" aria-label="Remove ' +
              escapeAttribute(title) +
              ' from the comparison">' + CLOSE_ICON + "</button>" +
              (product.image
                ? '<img src="' +
                  escapeAttribute(product.image) +
                  '" alt="" loading="lazy" class="h-16 w-full object-contain md:h-[110px]">'
                : '<div class="h-16 w-full md:h-[110px]" role="presentation"></div>') +
              '<span class="font-body text-xs font-normal leading-snug md:text-sm">' +
              titleHtml(title) +
              "</span>" +
              "</div>"
            );
          })
          .join("") +
        Array.from({ length: Math.max(maxSelectable() - count, 0) })
          .map(function () {
            return (
              '<div class="grid min-h-[120px] place-items-center rounded-[3px] bg-[#F2F2F2] p-2 text-center text-sm leading-tight text-gray-600 md:min-h-[170px]">' +
              "Add another product<br>to compare</div>"
            );
          })
          .join("");

      // No counts on the CTA. On mobile, where the note is hidden too, the CTA
      // says what is still needed.
      var compare = this.querySelector("[data-comparison-compare]");
      compare.disabled = count < 2;
      compare.textContent = count < 2 && deviceType() === "mobile" ? "Select one more bed" : "Compare";

      var note = this.querySelector("[data-comparison-tray-note]");
      if (count < 2) {
        note.textContent = "Select one more product to compare.";
      } else if (count >= maxSelectable()) {
        note.textContent =
          count + " products ready to compare. Remove one to swap in another.";
      } else {
        note.textContent = count + " products ready to compare.";
      }

      this.reserveSpace();
    }

    /**
     * Hidden while the panel is open; lifted by the collapsed title bar's
     * height (plus its gap from the viewport edge on desktop) while it is
     * collapsed; back in place when the tray is empty.
     */
    syncLaunchers() {
      var visible = handles.length > 0;
      var lift = visible && this.panel.hidden ? Math.round(window.innerHeight - this.getBoundingClientRect().top) : 0;
      placeLaunchers(visible && !this.panel.hidden, lift);
    }

    /**
     * Keep the tray from sitting on top of pagination or the end of the grid by
     * padding the page by exactly the tray's height while it is visible.
     */
    reserveSpace() {
      var visible = handles.length > 0;
      document.body.style.paddingBottom = visible ? this.offsetHeight + "px" : "";
    }
  }

  /* --------------------------------------------------------- the comparison */

  class ComparisonElement extends HTMLElement {
    connectedCallback() {
      var section = this;
      this.table = this.querySelector("[data-comparison-table]");
      this.empty = this.querySelector("[data-comparison-empty]");
      this.headerRow = this.querySelector("[data-comparison-header-row]");
      this.groups = this.querySelector("[data-comparison-groups]");
      this.drawer = this.querySelector("[data-comparison-drawer]");
      this.stickyBar = this.querySelector("[data-comparison-sticky-bar]");
      this.stickyRow = this.querySelector("[data-comparison-sticky-row]");
      this.one = this.dataset.itemSingular || "bed";
      this.many = this.dataset.itemPlural || "beds";
      this.renderToken = 0;

      // Delegated, because the empty comparison slots and the empty-state box
      // also open the drawer, and they are rebuilt on every paint.
      if (this.drawer) {
        this.addEventListener("click", function (event) {
          if (event.target.closest("[data-comparison-open-drawer]")) {
            section.drawer.showModal();
            // A modal <dialog> blocks clicks behind it but not scrolling.
            document.documentElement.classList.add("overflow-hidden");
          }
        });
        this.drawer.addEventListener("close", function () {
          document.documentElement.classList.remove("overflow-hidden");
        });
        this.querySelectorAll("[data-comparison-close-drawer]").forEach(function (button) {
          button.addEventListener("click", function () {
            section.drawer.close();
          });
        });
      }

      this.bindTooltips();
      this.bindStickyBar();
      this.render();
      subscribers.push(function () {
        section.render();
      });
    }

    /**
     * Tooltips open on click and close on Escape or an outside click, and are
     * plain buttons with aria-expanded so keyboard and screen-reader users get
     * the same content as a pointer hover would (FR-11).
     */
    bindTooltips() {
      var section = this;

      this.closeTooltips = function () {
        section.querySelectorAll("[data-comparison-tooltip]").forEach(function (button) {
          button.setAttribute("aria-expanded", "false");
          var tip = document.getElementById(button.getAttribute("aria-controls"));
          if (tip) tip.hidden = true;
        });
      };

      this.addEventListener("click", function (event) {
        var button = event.target.closest("[data-comparison-tooltip]");
        if (!button) {
          section.closeTooltips();
          return;
        }
        var open = button.getAttribute("aria-expanded") === "true";
        section.closeTooltips();
        if (open) return;
        button.setAttribute("aria-expanded", "true");
        var tip = document.getElementById(button.getAttribute("aria-controls"));
        if (tip) tip.hidden = false;
      });

      document.addEventListener("click", function (event) {
        if (!section.contains(event.target)) section.closeTooltips();
      });

      document.addEventListener("keydown", function (event) {
        if (event.key === "Escape") section.closeTooltips();
      });
    }

    /**
     * Shows the compact header while the full header row is scrolled off and
     * some of the table is still on screen. The site header hides on scroll
     * down and returns on scroll up (theme.js toggles .sticky-header.show), so
     * the bar is offset by the header's height only while it is showing, and
     * shares its 0.3s slide.
     */
    bindStickyBar() {
      if (!this.stickyBar) return;
      var section = this;
      var queued = false;

      this.updateStickyBar = function () {
        queued = false;
        var bar = section.stickyBar;
        if (section.table.hidden || !section.stickyRow.childElementCount) {
          bar.removeAttribute("data-visible");
          return;
        }

        var offset = 0;
        var siteHeader = document.querySelector(".sticky-header");
        if (siteHeader && siteHeader.classList.contains("show")) {
          // Measured from the wrapper, which doesn't move, rather than the
          // header itself, which may be mid-slide.
          var wrapper = siteHeader.closest(".sticky-header-wrapper") || siteHeader;
          offset = Math.max(wrapper.getBoundingClientRect().top, 0) + siteHeader.offsetHeight;
        }
        bar.style.transform = "translateY(" + offset + "px)";

        var headerBottom = section.headerRow.getBoundingClientRect().bottom;
        var tableBottom = section.table.getBoundingClientRect().bottom;
        var visible = headerBottom < offset && tableBottom > offset + bar.offsetHeight;
        bar.toggleAttribute("data-visible", visible);
      };

      function queue() {
        if (queued) return;
        queued = true;
        // A frame later, so theme.js has already toggled the header for this scroll.
        window.requestAnimationFrame(section.updateStickyBar);
      }

      window.addEventListener("scroll", queue, { passive: true });
      window.addEventListener("resize", queue);
    }

    /**
     * The funnel's "viewed a comparison" step: the first time this page paints
     * a table of two or more products. Once per page load, so swapping beds
     * around doesn't count as extra views.
     */
    trackPageView(usable) {
      if (this.pageViewSent) return;
      this.pageViewSent = true;
      track("comparison_page_view", {
        placement: this.dataset.placement,
        compared_products: usable.map(function (entry) { return entry.handle; }).join(","),
        product_count: usable.length
      });
    }

    render() {
      var section = this;
      var limit = maxSelectable();
      var selected = handles.slice(0, limit);

      this.updateDrawerStatus(limit);

      if (selected.length === 0) {
        this.table.hidden = true;
        if (this.updateStickyBar) this.updateStickyBar();
        this.empty.hidden = false;
        this.setEmpty(
          '<strong class="block text-base">Select two or more ' + escapeHtml(this.many) + " to compare.</strong>" +
            '<span class="mt-1 block text-gray-600">Your selected ' + escapeHtml(this.many) +
            " appear here with their durability, comfort, fit, warranty, personalization and feature details.</span>" +
            this.chooseButton("Select " + this.many),
          true
        );
        return;
      }

      // One product is not a comparison, but it must not be a dead end either:
      // keep the header row up so the empty slots stay visible and clickable,
      // and hide only the attribute rows (FR-25).
      if (selected.length === 1) {
        this.empty.hidden = false;
        this.setEmpty(
          '<strong class="block text-base">' +
            escapeHtml((meta[selected[0]] || {}).title || "One " + this.one) +
            " is selected.</strong>" +
            '<span class="mt-1 block text-gray-600">Choose one more ' + escapeHtml(this.one) +
            " to start your comparison.</span>" +
            this.chooseButton("Choose another " + this.one),
          true
        );

        var singleToken = ++this.renderToken;
        var singleSection = this;
        fetchColumn(selected[0]).then(function (column) {
          if (singleToken !== singleSection.renderToken || !column) return;
          singleSection.paint([{ handle: selected[0], column: column }], limit);
          if (singleSection.groups) singleSection.groups.hidden = true;
          // The empty slot beside the product is the prompt now; the box
          // above only covers the fetch (and stays if the column fails).
          singleSection.empty.hidden = true;
          singleSection.table.hidden = false;
          if (singleSection.updateStickyBar) singleSection.updateStickyBar();
        });
        return;
      }

      if (this.groups) this.groups.hidden = false;

      // The table stays hidden until its columns have actually painted.
      // Revealing the empty skeleton first and filling it later makes the
      // comparison appear and then vanish whenever a column fails to load.
      this.table.hidden = true;
      if (this.updateStickyBar) this.updateStickyBar();
      this.empty.hidden = false;
      this.empty.setAttribute("aria-busy", "true");
      this.setEmpty(
        '<strong class="block text-base">Loading your comparison…</strong>' +
          '<span class="mt-1 block text-gray-600">Fetching the details for ' +
          selected.length +
          " selected " + escapeHtml(this.many) + ".</span>",
        false
      );

      var token = ++this.renderToken;
      Promise.all(selected.map(fetchColumn)).then(function (columns) {
        // A newer render started while these were in flight.
        if (token !== section.renderToken) return;

        var usable = [];
        var missing = [];
        columns.forEach(function (column, index) {
          if (column) {
            usable.push({ handle: selected[index], column: column });
          } else {
            missing.push(selected[index]);
          }
        });

        section.empty.removeAttribute("aria-busy");

        // Too few columns survived to be a comparison. Say so — falling back
        // to the generic prompt would imply the shopper had selected nothing.
        if (usable.length < 2) {
          section.table.hidden = true;
          if (section.updateStickyBar) section.updateStickyBar();
          section.empty.hidden = false;
          var many = escapeHtml(section.many);
          section.setEmpty(
            (missing.length
              ? '<strong class="block text-base">Some ' + many + " are no longer available to compare.</strong>" +
                '<span class="mt-1 block text-gray-600">Choose two or more ' + many + " to see them side by side.</span>"
              : '<strong class="block text-base">Select two or more ' + many + " to compare.</strong>" +
                '<span class="mt-1 block text-gray-600">Your selected ' + many +
                " appear here with their durability, comfort, fit, warranty, personalization and feature details.</span>") +
              section.chooseButton("Select " + section.many),
            true
          );
          return;
        }

        section.paint(usable, limit);
        section.empty.hidden = true;
        section.table.hidden = false;
        section.trackPageView(usable);
        if (section.updateStickyBar) section.updateStickyBar();
      });
    }

    /**
     * Columns follow the selection, not the limit: two products get two
     * columns, three get three. A lone product keeps one empty slot beside it
     * so it doesn't stretch across the table and there's an obvious place to
     * add the second (FR-25). Adding a third to a pair goes through the
     * "Select" button, which the drawer caps at the limit.
     */
    paint(usable, limit) {
      var section = this;
      var columns = Math.min(Math.max(usable.length, 2), limit);
      this.table.style.setProperty("--cmp-cols", String(columns));
      this.headerRow.innerHTML = "";
      if (this.stickyRow) {
        this.stickyRow.innerHTML = "";
        this.stickyRow.style.setProperty("--cmp-cols", String(columns));
      }

      usable.forEach(function (entry) {
        var header = entry.column.querySelector("[data-comparison-header]");
        if (header) this.headerRow.appendChild(header.cloneNode(true));
        var sticky = entry.column.querySelector("[data-comparison-sticky]");
        if (sticky && this.stickyRow) this.stickyRow.appendChild(sticky.cloneNode(true));
      }, this);

      // An unfilled slot is the shopper's way to add the next product (FR-25).
      // Without a drawer configured there is nothing for it to open, so it
      // stays a plain prompt rather than a button that does nothing.
      var canOpenDrawer = !!this.drawer;
      for (var slot = usable.length; slot < columns; slot += 1) {
        var placeholder = document.createElement(canOpenDrawer ? "button" : "div");
        placeholder.className =
          "grid place-items-center p-4 text-center text-sm leading-tight text-gray-500 border-l border-gray-200" +
          (canOpenDrawer ? " hover:text-gray-900 hover:underline" : "");
        if (canOpenDrawer) {
          placeholder.type = "button";
          placeholder.setAttribute("data-comparison-open-drawer", "");
        }
        placeholder.innerHTML = "Choose another " + escapeHtml(this.one) + "<br>to compare";
        this.headerRow.appendChild(placeholder);
      }

      if (this.stickyRow) {
        for (var gap = usable.length; gap < columns; gap += 1) {
          var blank = document.createElement("div");
          blank.className = "border-l border-gray-200";
          this.stickyRow.appendChild(blank);
        }
      }

      this.querySelectorAll("[data-comparison-row]").forEach(function (row) {
        var key = row.dataset.comparisonRow;
        row.innerHTML = "";
        usable.forEach(function (entry) {
          var cell = entry.column.querySelector('[data-comparison-cell="' + key + '"]');
          row.appendChild(
            cell
              ? cell.cloneNode(true)
              : emptyCell()
          );
        });
      });

      this.querySelectorAll("[data-comparison-view-product]").forEach(function (link) {
        link.addEventListener("click", function (event) {
          var handle = link.dataset.comparisonViewProduct;
          trackThenNavigate(
            event,
            "comparison_view_product",
            {
              product_id: (meta[handle] || {}).id,
              product_title: (meta[handle] || {}).title,
              slot: handles.indexOf(handle) + 1,
              compared_products: handles.join(","),
              placement: section.dataset.placement
            },
            link.href
          );
        });
      });
    }

    /**
     * Fills the empty-state box. When `clickable` and a drawer exists, the whole
     * box opens it (via the delegated [data-comparison-open-drawer] handler);
     * the button inside stays as the keyboard and screen-reader route, so the
     * box itself takes no role.
     */
    setEmpty(html, clickable) {
      var open = clickable && !!this.drawer;
      this.empty.innerHTML = html;
      this.empty.toggleAttribute("data-comparison-open-drawer", open);
      ["cursor-pointer", "hover:border-gray-900", "hover:bg-gray-100"].forEach(function (name) {
        this.empty.classList.toggle(name, open);
      }, this);
    }

    /**
     * A "choose products" button, but only where the drawer exists to answer it.
     * Without a Selectable products collection configured there is nowhere to
     * send the shopper, so the prompt stays text rather than becoming a button
     * that does nothing.
     */
    chooseButton(label) {
      if (!this.drawer) return "";
      return (
        '<button type="button" data-comparison-open-drawer ' +
        'class="btn !mt-4 enabled:hover:!bg-[var(--colorBtnPrimaryDim)]">' +
        escapeHtml(label) +
        "</button>"
      );
    }

    updateDrawerStatus(limit) {
      var status = this.querySelector("[data-comparison-drawer-status]");
      if (!status) return;
      status.textContent = Math.min(handles.length, limit) + "/" + limit + " selected";
    }
  }

  function emptyCell() {
    var cell = document.createElement("div");
    cell.className =
      "flex min-h-[102px] flex-col items-center justify-center border-l border-gray-200 px-2.5 py-3.5 text-center text-sm text-gray-500 first:border-l-0 md:min-h-[110px] md:px-4";
    cell.textContent = "Not available";
    return cell;
  }

  function escapeHtml(value) {
    var div = document.createElement("div");
    div.textContent = value == null ? "" : String(value);
    return div.innerHTML;
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/"/g, "&quot;");
  }

  /* ------------------------------------------------------------ bootstrap */

  function restore() {
    var stored = readStorage();
    if (stored && stored.meta) meta = stored.meta;
    if (stored && Array.isArray(stored.handles)) handles = stored.handles;

    // The URL wins where it carries a comparison, so a shared or refreshed
    // link always shows what it says it shows. Only on a page that owns its
    // URL, though — a stray ?products= on a collection belongs to Shopify's
    // filtering, not to us.
    var fromUrl = urlHoldsSelection()
      ? new URL(window.location.href).searchParams.get("products")
      : null;
    if (fromUrl !== null) {
      handles = fromUrl
        .split(",")
        .map(function (handle) {
          return handle.trim();
        })
        .filter(Boolean);
    }

    handles = handles.filter(function (handle, index, list) {
      return list.indexOf(handle) === index;
    });
    handles = handles.slice(0, maxSelectable());
  }

  restore();
  subscribers.push(syncControls);

  // Collection filtering, sorting and paging swap #CollectionAjaxResult, so the
  // freshly rendered Compare controls need the current state applied to them.
  document.addEventListener("collection:reloaded", syncControls);

  // Crossing the desktop/mobile boundary changes the limit; trim rather than
  // silently comparing more products than the layout supports.
  window.matchMedia(DESKTOP_QUERY).addEventListener("change", function () {
    if (handles.length > maxSelectable()) {
      handles = handles.slice(0, maxSelectable());
    }
    commit();
  });

  if (!customElements.get("comparison-tray")) {
    customElements.define("comparison-tray", TrayElement);
  }
  if (!customElements.get("product-comparison")) {
    customElements.define("product-comparison", ComparisonElement);
  }

  syncControls();

  window.K9Comparison = {
    get handles() {
      return handles.slice();
    },
    select: function (handle, details, placement) {
      return toggle(handle, details, placement || "api");
    },
    clear: function () {
      clear("api");
    },
    max: maxSelectable
  };
})();
